import { type RefObject, useEffect } from "react";

/** Tabbable candidates inside a dialog panel. `[tabindex="-1"]` is excluded on
 *  purpose: those are programmatic focus targets (the panel itself), not stops
 *  in the Tab order. */
export const TABBABLE_SELECTOR = [
  "a[href]",
  "area[href]",
  "button:not([disabled])",
  "input:not([disabled]):not([type='hidden'])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "[contenteditable='true']",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

// LIFO of live focus traps: only the topmost one handles Tab, so a confirm
// modal opened above another modal doesn't have the lower trap yanking focus
// back into the covered panel (mount order == stacking order, same contract as
// useOverlay's escapeStack).
const focusTrapStack: symbol[] = [];

/**
 * Where a Tab press should move focus, expressed as an index into the panel's
 * tabbable list — or `null` to mean "focus is already mid-list inside the
 * panel, the browser's native Tab does the right thing".
 *
 * `activeIndex === -1` means focus is outside the tabbable list (the panel
 * itself, or something in the inert background), so the trap pulls it back to
 * the appropriate end rather than letting Tab escape into the page behind.
 */
export function resolveFocusTrapTarget(
  count: number,
  activeIndex: number,
  backwards: boolean,
): number | null {
  if (count === 0) {
    return null;
  }
  if (count === 1) {
    // One stop: Tab must not leave, so it re-focuses that stop.
    return 0;
  }
  if (backwards) {
    return activeIndex <= 0 ? count - 1 : null;
  }
  return activeIndex === -1 || activeIndex >= count - 1 ? 0 : null;
}

/** Tabbable descendants in DOM order, minus anything not actually rendered
 *  (a collapsed expander's buttons, `display: none` panes). */
export function collectTabbable(container: HTMLElement): HTMLElement[] {
  return [...container.querySelectorAll<HTMLElement>(TABBABLE_SELECTOR)].filter(
    (element) => element.getClientRects().length > 0,
  );
}

/**
 * Focus containment for a modal dialog: move focus into the panel on open,
 * keep Tab/Shift+Tab cycling within it, and return focus to whatever opened
 * the dialog on close.
 *
 * Scope note: this contains focus, it does not make the background inert.
 * Content behind the overlay is still reachable by screen-reader virtual
 * cursor and by clicks outside the panel (which the overlay turns into a
 * dismiss). Full inertness needs `inert`/`aria-hidden` ref-counted across the
 * app root and every stacked overlay tier — a larger change than this fix.
 */
export function useFocusTrap(
  containerRef: RefObject<HTMLElement | null>,
  active: boolean,
): void {
  useEffect(() => {
    const container = containerRef.current;
    if (!active || !container) {
      return;
    }
    const token = Symbol("focus-trap");
    focusTrapStack.push(token);
    const previouslyFocused =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    // Panel carries tabIndex={-1}, so an empty dialog still lands focus
    // inside itself instead of leaving it on the trigger behind the overlay.
    (collectTabbable(container)[0] ?? container).focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Tab" || focusTrapStack.at(-1) !== token) {
        return;
      }
      const tabbable = collectTabbable(container);
      const activeIndex =
        document.activeElement instanceof HTMLElement
          ? tabbable.indexOf(document.activeElement)
          : -1;
      const target = resolveFocusTrapTarget(
        tabbable.length,
        activeIndex,
        event.shiftKey,
      );
      if (target === null) {
        return;
      }
      event.preventDefault();
      tabbable[target]?.focus();
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      const index = focusTrapStack.indexOf(token);
      if (index !== -1) {
        focusTrapStack.splice(index, 1);
      }
      document.removeEventListener("keydown", handleKeyDown);
      // Only restore if the opener is still in the document — a dialog that
      // deleted the row it was opened from must not throw focus at a detached
      // node (that silently resets focus to <body>).
      if (previouslyFocused !== null && previouslyFocused.isConnected) {
        previouslyFocused.focus();
      }
    };
  }, [active, containerRef]);
}
